import { getResolvedRef, mergeSiblingReferences } from '@scalar/workspace-store/helpers/get-resolved-ref';
import { field, heading, inlineCode, item, list, paragraph, strong, text } from './markdown-nodes.js';
import { renderExamples } from './render-examples.js';
import { renderEncoding, renderHeaders, renderResponseLinks } from './render-operation-details.js';
import { renderSecurity } from './render-security.js';
/** Render effective operation context without mutating the prepared document. */
export const renderOperation = async (document, path, method, pathItem, operation, webhook, { description, schemas }) => {
    const openapiVersion = document['x-original-oas-version'] ?? document.openapi;
    const stability = operation['x-scalar-stability'];
    const title = (operation.summary || `${method.toUpperCase()} ${path}`) +
        (stability ? ` (${stability})` : operation.deprecated ? ' ⚠️ Deprecated' : '');
    const metadata = [
        field('Method', inlineCode(method.toUpperCase())),
        field(webhook ? 'Webhook' : 'Path', inlineCode(path)),
    ];
    if (operation.operationId)
        metadata.push(field('Operation ID', inlineCode(operation.operationId)));
    if (operation.tags)
        metadata.push(field('Tags', text(operation.tags.join(', '))));
    if (stability)
        metadata.push(field('Stability', text(stability)));
    const nodes = [heading(3, text(title)), list(metadata), ...(await description(operation.description))];
    const servers = operation.servers ?? pathItem.servers ?? document.servers;
    if (servers?.length) {
        nodes.push(heading(4, text('Effective servers')));
        const serverItems = [];
        for (const server of servers) {
            const blocks = [
                paragraph(inlineCode(server.url)),
                ...(await description(server.description)),
            ];
            const variables = Object.entries(server.variables ?? {});
            if (variables.length)
                blocks.push(list(variables.map(([name, variable]) => item(paragraph(text(`${name}: `), inlineCode(variable.default))))));
            serverItems.push(item(...blocks));
        }
        nodes.push(list(serverItems));
    }
    nodes.push(...(await renderSecurity(operation.security ?? document.security, document.components?.securitySchemes, description)));
    const parameters = new Map();
    for (const reference of [...(pathItem.parameters ?? []), ...(operation.parameters ?? [])]) {
        const parameter = getResolvedRef(reference, mergeSiblingReferences);
        if (parameter)
            parameters.set(`${parameter.in}:${parameter.name}`, parameter);
    }
    if (parameters.size)
        nodes.push(heading(4, text('Parameters')));
    for (const parameter of parameters.values()) {
        nodes.push(heading(5, inlineCode(parameter.name), text(`${parameter.required ? ' required' : ''}${parameter.deprecated ? ' deprecated' : ''}`)));
        const fields = [field('In', inlineCode(parameter.in))];
        if ('style' in parameter && parameter.style)
            fields.push(field('Style', inlineCode(parameter.style)));
        if ('explode' in parameter && typeof parameter.explode === 'boolean')
            fields.push(field('Explode', inlineCode(parameter.explode)));
        if (parameter.allowEmptyValue)
            fields.push(field('Allow Empty Value', text('true')));
        if ('allowReserved' in parameter && parameter.allowReserved)
            fields.push(field('Allow Reserved', text('true')));
        nodes.push(list(fields), ...(await description(parameter.description)));
        if ('schema' in parameter && parameter.schema !== undefined)
            nodes.push(...schemas.render(parameter.schema));
        if ('example' in parameter || 'examples' in parameter)
            nodes.push(...(await renderExamples({ example: parameter.example, examples: parameter.examples }, description, 'application/json', 'write', openapiVersion)));
        for (const [mediaType, content] of Object.entries('content' in parameter ? (parameter.content ?? {}) : {})) {
            nodes.push(heading(6, text(`Content-Type: ${mediaType}`)));
            if (content.schema !== undefined)
                nodes.push(...schemas.render(content.schema));
            nodes.push(...(await renderExamples(content, description, mediaType, 'write', openapiVersion)));
        }
    }
    const body = getResolvedRef(operation.requestBody, mergeSiblingReferences);
    if (body) {
        nodes.push(heading(4, text('Request Body')), ...(await description(body.description)));
        if (typeof body.required === 'boolean')
            nodes.push(paragraph(strong(text('Required:')), text(' '), inlineCode(body.required)));
        for (const [mediaType, content] of Object.entries(body.content ?? {})) {
            nodes.push(heading(5, text(`Content-Type: ${mediaType}`)));
            if (content.schema !== undefined)
                nodes.push(...schemas.render(content.schema));
            nodes.push(...(await renderExamples(content, description, mediaType, 'write', openapiVersion)));
            nodes.push(...(await renderEncoding(content.encoding, mediaType, description, schemas, openapiVersion)));
        }
    }
    const responses = Object.entries(operation.responses ?? {}).flatMap(([status, reference]) => {
        const response = getResolvedRef(reference, mergeSiblingReferences);
        return response ? [{ status, response }] : [];
    });
    if (responses.length)
        nodes.push(heading(4, text('Responses')));
    for (const { status, response } of responses) {
        nodes.push(heading(5, text(`Status: ${status}${response.description ? ` ${response.description}` : ''}`)));
        nodes.push(...(await renderHeaders(response.headers, description, schemas, openapiVersion)), ...(await renderResponseLinks(response.links, description)));
        for (const [mediaType, content] of Object.entries(response.content ?? {})) {
            nodes.push(heading(6, text(`Content-Type: ${mediaType}`)));
            if (content.schema !== undefined)
                nodes.push(...schemas.render(content.schema));
            nodes.push(...(await renderExamples(content, description, mediaType, 'read', openapiVersion)));
        }
    }
    return nodes;
};
